import { useMemo, useEffect, useState } from 'react'

import MuiLinearProgress from '@mui/material/LinearProgress'
import { DataGridPro as DataGrid, useGridApiRef } from '@mui/x-data-grid-pro'
import { LicenseInfo as MuiLicenseInfo } from '@mui/x-license-pro'

import { useGetProspectsQuery, useAssignProspectsToCampaignMutation } from '../slice'
import { useGetFieldsQuery } from '../../../services/fields/slice'
import { useGetCampaignsQuery } from '../../campaigns/slice'
import { useGetUsersQuery } from '../../users/slice'
import { useGetClientsQuery } from '../../clients/slice'
import { useGetAllDispositionsQuery } from '../../../services/dispositions/slice'

import AppConfig from '../../../config'
import CustomGridToolbar from './customGridToolbar'

MuiLicenseInfo.setLicenseKey(AppConfig.mui.licenseKey)

const ProspectsList = () => {
  const { data: prospects, isLoading: isProspectsLoading } = useGetProspectsQuery()
  const { data: fields, isLoading: isFieldsLoading } = useGetFieldsQuery('prospects')
  const { data: campaigns, isLoading: isCampaignsLoading } = useGetCampaignsQuery()
  const { data: users, isLoading: isUsersLoading } = useGetUsersQuery()
  const { data: clients, isLoading: isClientsLoading } = useGetClientsQuery()
  const { data: dispositions, isLoading: isDispositionsLoading } = useGetAllDispositionsQuery()

  const [{ isLoading: isProspectsUpdating }] = useAssignProspectsToCampaignMutation()
  const [selectedIds, setSelectedIds] = useState([])
  const [rows, setRows] = useState([])
  const apiRef = useGridApiRef()

  useEffect(() => {
    if (prospects && users && campaigns && clients && dispositions) {
      const prospectsWithCampaigns = prospects.map((prospect) => {
        const { campaigns: prospectCampaigns, ...prospectValues } = prospect
        if (!prospectCampaigns) return prospectValues
        const campaignMap = prospectCampaigns?.map((campaign) => {
          const { id, assignedTo, producerId, disposition, nextActionDate } = campaign
          const assignedToUser = users.find((user) => user.id === assignedTo)
          const thisCampaign = campaigns.find((c) => c.id === id)
          const client = thisCampaign && clients?.find((c) => c.id === thisCampaign.client)
          const producer = client?.producers?.find((p) => p.id === producerId)

          const assignedToName = assignedToUser ? `${assignedToUser.givenName} ${assignedToUser.familyName}` : '---'
          const producerName = producer ? producer?.name : '---'
          const dispositionLabel = dispositions.find((d) => d.code === disposition)?.label
          return {
            [`campaign.${id}_assignedTo`]: assignedToName,
            [`campaign.${id}_producer`]: producerName,
            [`campaign.${id}_disposition`]: dispositionLabel,
            [`campaign.${id}_nextActionDate`]: nextActionDate ? new Date(nextActionDate) : null,
          }
        })
        const campaignValues = Object.assign({}, ...campaignMap)
        return { ...prospectValues, ...campaignValues }
      })
      setRows(prospectsWithCampaigns)
    }
  }, [prospects, users, campaigns, clients, dispositions])

  const handleSelectionModelChange = (params) => {
    setSelectedIds(params)
  }

  const columns = useMemo(() => {
    if (fields && prospects && campaigns && users) {
      const campaignFields = campaigns
        .map((campaign, index) => {
          return [
            {
              field: `campaign.${campaign.id}_assignedTo`,
              headerName: `[${campaign.code || campaign.name}] Assignee`,
              description: `Assigned to BDR for ${campaign.name}`,
              width: 180,
              editable: false,
              priority: 200000 + index * 10,
            },
            {
              field: `campaign.${campaign.id}_producer`,
              headerName: `[${campaign.code || campaign.name}] Producer`,
              description: `Assigned to Producer for ${campaign.name}`,
              width: 180,
              editable: false,
              priority: 200001 + index * 10,
            },
            {
              field: `campaign.${campaign.id}_disposition`,
              headerName: `[${campaign.code || campaign.name}] Disposition`,
              description: `Disposition for ${campaign.name}`,
              width: 150,
              editable: false,
              priority: 200002 + index * 10,
            },
            {
              field: `campaign.${campaign.id}_nextActionDate`,
              headerName: `[${campaign.code || campaign.name}] Next Action`,
              description: `Next action date for ${campaign.name}`,
              width: 180,
              editable: false,
              priority: 200003 + index * 10,
              type: 'dateTime',
            },
          ]
        })
        .flat()
      const prospectFields = fields.map((prospectField) => {
        const type =
          prospectField.type === 'boolean' ? 'boolean' : prospectField.type === 'date' ? 'dateTime' : undefined
        return {
          field: prospectField.name,
          headerName: prospectField.label,
          description: prospectField.description,
          width: prospectField.width || 200,
          editable: !prospectField.isReadOnly,
          type,
          priority: prospectField.priority || 1000,
          valueGetter: (params) => {
            if (params.colDef.type === 'dateTime' && params.value) return new Date(params.value)
            return params.value
          },
        }
      })
      const allFields = [...campaignFields, ...prospectFields]
      const sortedFields = allFields.sort((a, b) => a.priority - b.priority)
      return sortedFields
    }
    return []
  }, [fields, prospects, campaigns, users])

  return (
    <>
      <DataGrid
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        density="compact"
        loading={
          isProspectsLoading ||
          isFieldsLoading ||
          isCampaignsLoading ||
          isUsersLoading ||
          isProspectsUpdating ||
          isClientsLoading ||
          isDispositionsLoading
        }
        pagination
        onRowSelectionModelChange={handleSelectionModelChange}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 25,
            },
          },
        }}
        slots={{
          loadingOverlay: MuiLinearProgress,
          toolbar: CustomGridToolbar,
        }}
        slotProps={{
          toolbar: {
            selectedIds,
          },
        }}
        pageSizeOptions={[25, 50, 100]}
        checkboxSelection
        // disableRowSelectionOnClick
      />
    </>
  )
}

export default ProspectsList
